import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { type UIIconName, UI_REGISTRY } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Leading icon — name from @bravemove/design-tokens/icons UI family. */
  readonly icon?: UIIconName;
  /** Trailing icon — name from @bravemove/design-tokens/icons UI family. */
  readonly trailing?: UIIconName;
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly children?: ReactNode;
  readonly type?: 'button' | 'submit' | 'reset';
}

/* Base — radius 10 (matches BraveMoveButton spec), inline-flex, font 700, body family,
 * 120 ms ease-out transitions, focus-visible 3 px ring (var(--color-accent) at .25 alpha). */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-[10px] font-bold whitespace-nowrap ' +
  'transition-[color,background-color,border-color,box-shadow,transform] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text ' +
  'motion-safe:active:scale-[0.98] ' +
  'disabled:opacity-45 disabled:cursor-not-allowed disabled:active:scale-100';

const SIZE: Record<ButtonSize, string> = {
  // px from system-forms.jsx sizeMap × text-[Npx] for font-size and h-[N] for height
  xs: 'h-7 px-2.5 py-1.5 text-[11.5px] [&_svg]:size-[13px]',
  sm: 'h-[34px] px-3 py-2 text-[12.5px] [&_svg]:size-[14px]',
  md: 'h-10 px-4 py-2.5 text-[13.5px] [&_svg]:size-[16px]',
  lg: 'h-12 px-[22px] py-3.5 text-[15px] [&_svg]:size-[18px]',
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-ink hover:bg-primary-hover',
  secondary: 'bg-surface text-ink border border-hairline-2 hover:bg-surface-2',
  ghost: 'bg-transparent text-ink hover:bg-surface-2',
  destructive: 'bg-danger text-danger-ink hover:brightness-90',
  // Link variant is intentionally outside SIZE — no padding, no height
  link: 'bg-transparent text-accent-text underline underline-offset-[3px] hover:opacity-80',
};

const LINK_SIZE: Record<ButtonSize, string> = {
  xs: 'text-[11.5px] [&_svg]:size-[13px]',
  sm: 'text-[12.5px] [&_svg]:size-[14px]',
  md: 'text-[13.5px] [&_svg]:size-[16px]',
  lg: 'text-[15px] [&_svg]:size-[18px]',
};

/** Shared button treatment for native anchors and router links, without nested controls. */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: Pick<ButtonProps, 'variant' | 'size' | 'fullWidth' | 'className'> = {}): string {
  return cn(
    BASE,
    variant === 'link' ? LINK_SIZE[size] : SIZE[size],
    VARIANT[variant],
    fullWidth && 'w-full',
    className,
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('motion-safe:animate-spin', className)}
      aria-hidden="true"
      data-testid="button-spinner"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        opacity="0.25"
      />
      <path
        d="M 12 3 A 9 9 0 0 1 21 12"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    trailing,
    loading = false,
    fullWidth = false,
    disabled,
    children,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  const Leading = icon ? UI_REGISTRY[icon] : null;
  const Trailing = trailing ? UI_REGISTRY[trailing] : null;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        buttonClassName({ variant, size, fullWidth }),
        loading && 'cursor-progress',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : Leading && <Leading color="currentColor" />}
      {children}
      {!loading && Trailing && <Trailing color="currentColor" />}
    </button>
  );
});
